import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    IconButton,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';

const SendMessageModal = ({ 
    open, 
    onClose, 
    onSend, 
    inquiryId, 
    toEmail, 
    defaultSubject = '',
    parentMessageId = null,
    sending = false
}) => {
    const [subject, setSubject] = useState(defaultSubject);
    const [body, setBody] = useState('');
    const [files, setFiles] = useState([]);
    const [errors, setErrors] = useState({});

    const handleFileSelect = (event) => {
        const selectedFiles = Array.from(event.target.files);
        setFiles([...files, ...selectedFiles]);
    };

    const handleRemoveFile = (index) => {
        setFiles(files.filter((_, i) => i !== index));
    };

    const validate = () => {
        const newErrors = {};
        
        if (!subject.trim()) {
            newErrors.subject = 'נושא חובה';
        }
        
        if (!body.trim()) {
            newErrors.body = 'תוכן ההודעה חובה';
        }
        
        if (!toEmail) {
            newErrors.toEmail = 'כתובת מייל חסרה';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSend = () => {
        if (!validate()) {
            return;
        }

        const messageData = {
            inquiryId,
            subject,
            body,
            toEmail,
            parentMessageId
        };

        onSend({ messageData, files });
        
        // Reset form
        setSubject('');
        setBody('');
        setFiles([]);
        setErrors({});
    };

    const handleClose = () => {
        setSubject(defaultSubject);
        setBody('');
        setFiles([]);
        setErrors({});
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            dir="rtl"
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">
                        {parentMessageId ? 'תגובה להודעה' : 'שליחת הודעה חדשה'}
                    </Typography>
                    <IconButton onClick={handleClose} disabled={sending}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    אל: {toEmail}
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Subject */}
                    <TextField
                        label="נושא"
                        fullWidth
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        error={!!errors.subject}
                        helperText={errors.subject}
                        disabled={sending}
                        required
                    />

                    {/* Body */}
                    <TextField
                        label="תוכן ההודעה"
                        fullWidth
                        multiline
                        rows={8}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        error={!!errors.body}
                        helperText={errors.body}
                        disabled={sending}
                        required
                    />

                    {/* File Upload */}
                    <Box>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<AttachFileIcon />}
                            disabled={sending}
                        >
                            צרף קבצים
                            <input
                                hidden
                                multiple
                                type="file"
                                onChange={handleFileSelect}
                            />
                        </Button>
                    </Box>

                    {/* Files List */}
                    {files.length > 0 && (
                        <List dense>
                            {files.map((file, index) => (
                                <ListItem key={index}>
                                    <ListItemText
                                        primary={file.name}
                                        secondary={`${(file.size / 1024).toFixed(2)} KB`}
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            edge="end"
                                            onClick={() => handleRemoveFile(index)}
                                            disabled={sending}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={sending}>
                    ביטול
                </Button>
                <Button
                    onClick={handleSend}
                    variant="contained"
                    startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
                    disabled={sending}
                >
                    {sending ? 'שולח...' : 'שלח הודעה'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SendMessageModal;

